import React, { useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import DropdownSelect from '../../components/DropdownSelect';
import { SnackbarSeverityType } from '../../enums';
import { SchoolClassesConfig } from '../../types/schoolConfigType';
import { StyledButton } from '../Exams/styled';
import AcademicTermsTableAndActions from './components/AcademicTermsTableAndActions';
import ConfirmationDialog from '../../components/ConfirmationDialog/ConfirmationDialog';
import AddAcademicTermModal from './components/AddAcademicTermModal';
import EditAcademicTermModal from './components/EditAcademicTermModal';
import { academicYears } from '../../constants/academicYears';
import { SearchContainer } from './styled';
import { useAcademicTermsMutations } from './config/useAcademicTermsMutations';
import { useAcademicTermsData } from './config/useAcademicTermsData';
import { useAcademicTermsSearch } from './config/useAcademicTermSearch';
import { AcademicTerm } from './types';
import Icons from '../../components/icons';
import { PageWrapper, Title } from '../common/styled';
import { ButtonContainer } from '../Staff/styled';

interface AcademicTermsPageProps {
  allClasses?: SchoolClassesConfig;
  school: string;
  onShowToast: (message: string, severity: SnackbarSeverityType) => void;
}

const AcademicTermsPage: React.FC<AcademicTermsPageProps> = ({
  allClasses,
  school,
  onShowToast
}) => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);

  const {
    academicYear,
    selectedGrade,
    selectedDivision,
    gradeOption,
    divisionOption,
    setAcademicYear,
    setSelectedGrade,
    setSelectedDivision,
    setDivisionOptions
  } = useAcademicTermsSearch(allClasses);
  const {
    academicTerms,
    availableTermsOptions,
    isSearched,
    isSubmitting,
    previousAcademicTerms,
    setAcademicTerms,
    handleSearch,
    setIsSearched
  } = useAcademicTermsData({
    school,
    academicYear: academicYear,
    selectedGrade: selectedGrade,
    selectedDivision: selectedDivision,
    onShowToast
  });

  const { selectedAcademicTerm, setSelectedAcademicTerm, handleDeleteConfirm } =
    useAcademicTermsMutations({
      school,
      academicYear: academicYear,
      selectedGrade: selectedGrade,
      selectedDivision: selectedDivision,
      academicTerms: previousAcademicTerms,
      onShowToast,
      refetch: handleSearch,
      handleDeleteModalClose: setDeleteModalOpen
    });

  const handleApiSuccessAndFailure = (isSuccess: boolean, mode: string) => {
    setIsAddModalOpen(false);
    setIsEditModalOpen(false);
    const message = isSuccess
      ? `Academic Term ${mode === 'add' ? 'added' : 'updated'} successfully`
      : `Error ${mode === 'add' ? 'adding' : 'updating'} academic term`;
    onShowToast(
      message,
      isSuccess ? SnackbarSeverityType.success : SnackbarSeverityType.error
    );
    handleSearch();
  };

  const handleEditOpenModal = (academicTerm: AcademicTerm) => {
    setSelectedAcademicTerm(academicTerm);
    setIsEditModalOpen(true);
  };

  const handleEditCloseModal = () => {
    setIsEditModalOpen(false);
    setSelectedAcademicTerm(null);
  };

  const handleDeleteClick = (academicTerm: AcademicTerm) => {
    setSelectedAcademicTerm(academicTerm);
    setDeleteModalOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setSelectedAcademicTerm(null);
  };

  const handleAcademicYearChange = (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | (Event & { target: { value: unknown; name: string } })
  ) => {
    setAcademicYear(event.target.value as string);
    setAcademicTerms([]);
    setIsSearched(false);
  };

  const handleGradeSearchChange = (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | (Event & { target: { value: unknown; name: string } })
  ) => {
    setSelectedGrade(event.target.value as string);
    setSelectedDivision('');
    setDivisionOptions([]);
    setAcademicTerms([]);
    setIsSearched(false);
  };

  return (
    <>
      <Title>Academic Terms Management</Title>
      <Box>
        <PageWrapper sx={{ minHeight: 'auto !important' }}>
          <SearchContainer>
            <DropdownSelect
              label='Academic Year'
              placeholder='Select Academic Year'
              name='academicYear'
              value={academicYear}
              options={academicYears}
              onChange={handleAcademicYearChange}
            />
            <DropdownSelect
              label='Grade'
              placeholder='Select Grade'
              name='grade'
              value={selectedGrade}
              options={gradeOption}
              onChange={handleGradeSearchChange}
            />
            <DropdownSelect
              label='Division'
              placeholder='Select Division'
              name='division'
              value={selectedDivision}
              options={divisionOption}
              onChange={(e) => {
                setSelectedDivision(e.target.value as string);
              }}
            />
            <ButtonContainer>
              <StyledButton
                onClick={handleSearch}
                disabled={
                  !academicYear ||
                  !selectedGrade ||
                  !selectedDivision ||
                  isSubmitting
                }
              >
                <Icons.SearchWhiteOutlined style={{ marginRight: '6px' }} />
                {isSubmitting ? (
                  <CircularProgress size={24} color='inherit' />
                ) : (
                  'Search'
                )}
              </StyledButton>
            </ButtonContainer>
          </SearchContainer>
        </PageWrapper>

        {academicYear && selectedGrade && selectedDivision && isSearched && (
          <AcademicTermsTableAndActions
            academicTerms={academicTerms}
            isLoading={isSubmitting}
            onEdit={handleEditOpenModal}
            onDelete={handleDeleteClick}
            onAddAcademicTerm={setIsAddModalOpen}
          />
        )}

        <AddAcademicTermModal
          fetchData={handleSearch}
          open={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          onApiSuccessAndFailure={handleApiSuccessAndFailure}
          academicYear={academicYear}
          grade={selectedGrade}
          division={selectedDivision}
          availableAcademicYearTermOption={availableTermsOptions}
          school={school}
          previousAcademicTerms={previousAcademicTerms}
        />

        <EditAcademicTermModal
          fetchData={handleSearch}
          open={isEditModalOpen}
          onClose={handleEditCloseModal}
          onApiSuccessAndFailure={handleApiSuccessAndFailure}
          academicTerm={selectedAcademicTerm}
          academicYear={academicYear}
          grade={selectedGrade}
          division={selectedDivision}
          availableAcademicYearTermOption={availableTermsOptions}
          school={school}
          previousAcademicTerms={previousAcademicTerms}
        />

        <ConfirmationDialog
          open={isDeleteModalOpen}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          message={`Are you sure you want to delete "${
            selectedAcademicTerm?.name || ''
          }"?`}
        />
      </Box>
    </>
  );
};

export default AcademicTermsPage;
